import { useEffect, useRef, useState } from 'react';
import { Action, AdaptiveCard } from 'adaptivecards';
import 'adaptivecards/lib/adaptivecards.css';

interface AdaptiveCardBubbleProps {
  /** Raw Adaptive Card JSON (from a Copilot Studio bot or rich agent message). */
  card: unknown;
  disabled?: boolean;
  /**
   * Called on Action.Submit with the action's data payload. Phase 2 wires this to the outbound
   * card-action endpoint; when omitted, Submit is a no-op (Phase 1 renders cards read-only).
   */
  onSubmit?: (value: unknown) => void;
}

type ActionWithExtras = Action & { url?: string; data?: unknown; getJsonTypeName?: () => string };

/**
 * Renders a bot/agent Adaptive Card in a live chat bubble. Security: Action.OpenUrl is validated to
 * http(s) and opened with noopener/noreferrer in a new tab; Action.Submit is surfaced via onSubmit
 * (Phase 2). Unknown action types are ignored. Mirrors PreChatSurveyCard but generic (no survey chrome).
 */
export function AdaptiveCardBubble({ card, disabled = false, onSubmit }: AdaptiveCardBubbleProps) {
  const hostRef = useRef<HTMLDivElement>(null);
  const onSubmitRef = useRef(onSubmit);
  const [renderError, setRenderError] = useState<string | null>(null);

  useEffect(() => { onSubmitRef.current = onSubmit; }, [onSubmit]);

  useEffect(() => {
    const styleId = 'cmsp-live-card-style';
    if (document.getElementById(styleId)) return;
    const style = document.createElement('style');
    style.id = styleId;
    style.textContent = `
      .cmsp-live-card .ac-adaptiveCard { box-sizing: border-box; width: 100% !important; padding: 0 !important; font-family: Segoe UI, system-ui, sans-serif; }
      .cmsp-live-card .ac-textBlock { color: #242424; }
      .cmsp-live-card .ac-input { box-sizing: border-box; width: 100%; border: 1px solid #d1d5db !important; border-radius: 8px !important; padding: 8px 10px !important; font: inherit !important; }
      .cmsp-live-card .ac-pushButton { min-height: 34px; border: 1px solid #5b5fc7; border-radius: 8px; padding: 6px 14px; background: #5b5fc7; color: #fff; font: inherit; font-weight: 600; cursor: pointer; }
      .cmsp-live-card .ac-pushButton:hover { background: #464775; border-color: #464775; }
      .cmsp-live-card[aria-disabled="true"] { opacity: 0.65; pointer-events: none; }
    `;
    document.head.appendChild(style);
  }, []);

  useEffect(() => {
    const host = hostRef.current;
    if (!host) return;
    host.replaceChildren();
    setRenderError(null);
    try {
      const adaptiveCard = new AdaptiveCard();
      adaptiveCard.onExecuteAction = (action: Action) => {
        const a = action as ActionWithExtras;
        const typeName = a.getJsonTypeName?.() ?? '';
        if (typeName === 'Action.OpenUrl' && typeof a.url === 'string') {
          try {
            const parsed = new URL(a.url);
            // Only https/http links; sever the opener so the opened tab can't drive this window.
            if (parsed.protocol === 'https:' || parsed.protocol === 'http:') {
              const win = window.open(a.url, '_blank', 'noopener,noreferrer');
              if (win) win.opener = null;
            }
          } catch {
            /* ignore malformed url */
          }
          return;
        }
        if (typeName === 'Action.Submit') {
          onSubmitRef.current?.(a.data ?? {});
        }
      };
      adaptiveCard.parse(card);
      const rendered = adaptiveCard.render();
      if (rendered) {
        host.appendChild(rendered);
      } else {
        setRenderError('This card could not be displayed.');
      }
    } catch (error) {
      setRenderError((error as Error).message);
    }
    return () => host.replaceChildren();
  }, [card]);

  return (
    <div style={styles.wrapper}>
      <div ref={hostRef} className="cmsp-live-card" style={styles.card} aria-disabled={disabled} />
      {renderError && <div style={styles.error}>{renderError}</div>}
    </div>
  );
}

interface SuggestedAction { title: string; value: unknown; type: string }

/**
 * Derive a message string from an Adaptive Card Action.Submit value (Phase 2). Over a text channel we
 * relay the submit through the normal message path. Prefer a human/bot-readable field when present,
 * else send the value as JSON so a bot reading structured input can still parse it. The exact wire
 * format for structured Copilot Studio card submits is refined once a post-login card is captured.
 */
export function cardSubmitToText(value: unknown): string {
  if (value == null) return '';
  if (typeof value === 'string') return value;
  if (typeof value === 'object') {
    const v = value as Record<string, unknown>;
    for (const key of ['text', 'title', 'displayText', 'label', 'value']) {
      if (typeof v[key] === 'string' && v[key]) return v[key] as string;
    }
    try { return JSON.stringify(value); } catch { return String(value); }
  }
  return String(value);
}

/** Suggested-action chips. Clicking sends the action's text value as a normal live message (Phase 1). */
export function SuggestedActionChips({ actions, disabled, onPick }: {
  actions: SuggestedAction[];
  disabled?: boolean;
  onPick: (text: string) => void;
}) {
  if (!actions || actions.length === 0) return null;
  return (
    <div style={styles.chips}>
      {actions.map((a, i) => {
        const label = a.title || String(a.value ?? '');
        const text = typeof a.value === 'string' && a.value ? a.value : label;
        return (
          <button
            key={`${label}-${i}`}
            type="button"
            disabled={disabled}
            style={styles.chip}
            onClick={() => onPick(text)}
          >
            {label}
          </button>
        );
      })}
    </div>
  );
}

const styles = {
  wrapper: { marginTop: 6, maxWidth: '100%' } as React.CSSProperties,
  card: {
    border: '1px solid #e1dfdd',
    borderRadius: 10,
    background: '#fff',
    padding: 12,
  } as React.CSSProperties,
  error: { marginTop: 6, color: '#b91c1c', fontSize: 12 } as React.CSSProperties,
  chips: { display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 } as React.CSSProperties,
  chip: {
    border: '1px solid #5b5fc7',
    color: '#5b5fc7',
    background: '#fff',
    borderRadius: 16,
    padding: '6px 14px',
    fontSize: 13,
    fontWeight: 600,
    cursor: 'pointer',
  } as React.CSSProperties,
  signinCard: {
    marginTop: 6,
    border: '1px solid #e1dfdd',
    borderRadius: 10,
    background: '#fff',
    padding: 12,
  } as React.CSSProperties,
  signinText: { fontSize: 13, color: '#242424', marginBottom: 10 } as React.CSSProperties,
  signinButton: {
    display: 'inline-block',
    border: '1px solid #5b5fc7',
    borderRadius: 8,
    padding: '8px 16px',
    background: '#5b5fc7',
    color: '#fff',
    fontSize: 13,
    fontWeight: 700,
    cursor: 'pointer',
  } as React.CSSProperties,
  signinHint: { fontSize: 12, color: '#616161', marginTop: 8 } as React.CSSProperties,
};

/**
 * OAuth sign-in card from a Copilot Studio bot. Opens the bot's sign-in URL in a popup (the one
 * genuinely browser-mediated step). Security: the URL must be HTTPS and its host must be on the
 * expected-auth allowlist, so a misconfigured/compromised workstream can't surface a phishing sign-in.
 * After signing in the user receives a magic code to type back into the chat, which the bot validates
 * over the normal text path — so no extra outbound endpoint is needed.
 */
export function SignInCard({ text, url, disabled }: { text: string; url: string; disabled?: boolean }) {
  const allowed = isAllowedAuthUrl(url);
  const openSignIn = () => {
    if (!allowed) return;
    // Open then sever the opener reference (window.open "noopener" feature is unreliable across
    // browsers) so the sign-in tab can never navigate this window via window.opener.
    const win = window.open(url, 'oc-bot-signin', 'popup,width=520,height=640,noopener,noreferrer');
    if (win) win.opener = null;
  };
  return (
    <div style={styles.signinCard}>
      <div style={styles.signinText}>{text || 'Please sign in to continue.'}</div>
      <button type="button" style={styles.signinButton} disabled={disabled || !allowed} onClick={openSignIn}>
        Sign in
      </button>
      {allowed
        ? <div style={styles.signinHint}>After signing in, enter the code you receive here to continue.</div>
        : <div style={styles.error}>This sign-in link could not be verified and was blocked for your safety.</div>}
    </div>
  );
}

// Expected identity-provider / Bot Framework auth hosts. A bot sign-in URL must be HTTPS and match one
// of these (exact host or subdomain). token.botframework.com covers its regional subdomains
// (europe./unitedstates.) via the subdomain rule.
const ALLOWED_AUTH_HOSTS = [
  'login.microsoftonline.com',
  'login.live.com',
  'login.microsoft.com',
  'token.botframework.com',
];

function isAllowedAuthUrl(url: string): boolean {
  if (!url) return false;
  try {
    const u = new URL(url);
    if (u.protocol !== 'https:') return false;
    const host = u.hostname.toLowerCase();
    return ALLOWED_AUTH_HOSTS.some((h) => host === h || host.endsWith(`.${h}`));
  } catch {
    return false;
  }
}
